import { useState, useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import {
  X, Undo2, Redo2, Type, SmilePlus, PenLine, Wand2, Trash2, MapPin, AtSign, Hash, Check, Search
} from 'lucide-react';
import { Slider } from '@/components/ui/slider';
import { api } from '@/App';
import {
  createImage, FILTER_PRESETS, DEFAULT_ADJUST, buildFilter, FONTS, TEXT_COLORS, ADE_STICKERS, EMOJIS, exportEditedImage
} from './editorUtils';

const GIPHY_KEY = process.env.REACT_APP_GIPHY_KEY;
const DRAW_COLORS = ['#FFFFFF', '#000000', '#FF3B5C', '#FFD60A', '#34C759', '#0A84FF', '#BF5AF2'];
const DRAW_WIDTHS = [3, 6, 12];
let idCounter = 1;

export default function MediaEditor({ open, imageSrc, onClose, onDone, mode = 'post' }) {
  const [stage, setStage] = useState({ w: 0, h: 0 });
  const [elements, setElements] = useState([]);
  const [strokes, setStrokes] = useState([]);
  const [preset, setPreset] = useState('none');
  const [adjust, setAdjust] = useState(DEFAULT_ADJUST);
  const [history, setHistory] = useState([]);
  const [future, setFuture] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [panel, setPanel] = useState(null); // 'sticker' | 'filters'
  const [stickerTab, setStickerTab] = useState('emoji');
  const [filterTab, setFilterTab] = useState('filters');
  const [drawMode, setDrawMode] = useState(false);
  const [drawColor, setDrawColor] = useState('#FFFFFF');
  const [drawWidth, setDrawWidth] = useState(6);
  const [textEdit, setTextEdit] = useState(null); // {id?, content, color, font}
  const [exporting, setExporting] = useState(false);
  const [connections, setConnections] = useState(null);
  const [mentionQuery, setMentionQuery] = useState('');
  const [hashtagInput, setHashtagInput] = useState('');
  const [locationInput, setLocationInput] = useState('');
  const [gifQuery, setGifQuery] = useState('');
  const [gifResults, setGifResults] = useState([]);

  const stageRef = useRef(null);
  const drawCanvasRef = useRef(null);
  const pointersRef = useRef(new Map());
  const currentStrokeRef = useRef(null);
  const lastTapRef = useRef({ id: null, time: 0 });

  // Reset when opening a new image + compute stage dims from natural aspect ratio
  useEffect(() => {
    if (!open || !imageSrc) return;
    setElements([]); setStrokes([]); setPreset('none'); setAdjust(DEFAULT_ADJUST);
    setHistory([]); setFuture([]); setSelectedId(null); setPanel(null); setDrawMode(false); setTextEdit(null);
    const compute = async () => {
      try {
        const img = await createImage(imageSrc);
        const ratio = img.naturalWidth / img.naturalHeight;
        const availW = Math.min(window.innerWidth, 520);
        const availH = (window.visualViewport?.height || window.innerHeight) - 160;
        let w = availW, h = w / ratio;
        if (h > availH) { h = availH; w = h * ratio; }
        setStage({ w: Math.round(w), h: Math.round(h) });
      } catch (e) { /* keep zero stage */ }
    };
    compute();
  }, [open, imageSrc]);

  // === History ===
  const snapshot = useCallback(() => ({
    elements: JSON.parse(JSON.stringify(elements)),
    strokes: JSON.parse(JSON.stringify(strokes)),
    preset, adjust: { ...adjust },
  }), [elements, strokes, preset, adjust]);

  const pushHistory = useCallback(() => {
    setHistory((h) => [...h.slice(-30), snapshot()]);
    setFuture([]);
  }, [snapshot]);

  const applySnap = (s) => {
    setElements(s.elements); setStrokes(s.strokes); setPreset(s.preset); setAdjust(s.adjust);
  };

  const undo = () => {
    setHistory((h) => {
      if (!h.length) return h;
      setFuture((f) => [...f, snapshot()]);
      applySnap(h[h.length - 1]);
      return h.slice(0, -1);
    });
    setSelectedId(null);
  };

  const redo = () => {
    setFuture((f) => {
      if (!f.length) return f;
      setHistory((h) => [...h, snapshot()]);
      applySnap(f[f.length - 1]);
      return f.slice(0, -1);
    });
  };

  // === Add elements ===
  const addElement = (partial) => {
    pushHistory();
    const n = elements.length % 5;
    const el = {
      id: `el_${idCounter++}`,
      x: stage.w / 2 + n * 18 - 36, y: stage.h / 2 + n * 18 - 36, scale: 1, rotation: 0,
      ...partial,
    };
    setElements((els) => [...els, el]);
    setSelectedId(el.id);
    setPanel(null);
  };

  const deleteSelected = () => {
    if (!selectedId) return;
    pushHistory();
    setElements((els) => els.filter((e) => e.id !== selectedId));
    setSelectedId(null);
  };

  // === Element gestures (drag / pinch-zoom / rotate) ===
  const onElPointerDown = (e, id) => {
    if (drawMode) return;
    e.stopPropagation();
    e.currentTarget.setPointerCapture(e.pointerId);
    if (pointersRef.current.size === 0) pushHistory();
    pointersRef.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
    setSelectedId(id);
    // double-tap text to edit
    const now = Date.now();
    if (lastTapRef.current.id === id && now - lastTapRef.current.time < 320) {
      const el = elements.find((el) => el.id === id);
      if (el?.type === 'text') setTextEdit({ id: el.id, content: el.content, color: el.color, font: el.font });
    }
    lastTapRef.current = { id, time: now };
  };

  const onElPointerMove = (e, id) => {
    if (!pointersRef.current.has(e.pointerId)) return;
    const prevPts = new Map(pointersRef.current);
    pointersRef.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
    const currPts = pointersRef.current;

    setElements((els) => els.map((el) => {
      if (el.id !== id) return el;
      if (currPts.size === 1) {
        const prev = prevPts.get(e.pointerId);
        return { ...el, x: el.x + (e.clientX - prev.x), y: el.y + (e.clientY - prev.y) };
      }
      const ids = [...currPts.keys()].slice(0, 2);
      const p1p = prevPts.get(ids[0]), p2p = prevPts.get(ids[1]);
      const p1c = currPts.get(ids[0]), p2c = currPts.get(ids[1]);
      if (!p1p || !p2p) return el;
      const prevDist = Math.hypot(p2p.x - p1p.x, p2p.y - p1p.y);
      const newDist = Math.hypot(p2c.x - p1c.x, p2c.y - p1c.y);
      const dScale = prevDist > 0 ? newDist / prevDist : 1;
      const dAngle = Math.atan2(p2c.y - p1c.y, p2c.x - p1c.x) - Math.atan2(p2p.y - p1p.y, p2p.x - p1p.x);
      const midDx = (p1c.x + p2c.x - p1p.x - p2p.x) / 2;
      const midDy = (p1c.y + p2c.y - p1p.y - p2p.y) / 2;
      return {
        ...el,
        x: el.x + midDx, y: el.y + midDy,
        scale: Math.min(8, Math.max(0.3, el.scale * dScale)),
        rotation: (el.rotation || 0) + dAngle,
      };
    }));
  };

  const onElPointerUp = (e) => {
    pointersRef.current.delete(e.pointerId);
  };

  // === Drawing ===
  const stagePoint = (e) => {
    const rect = stageRef.current.getBoundingClientRect();
    return { x: e.clientX - rect.left, y: e.clientY - rect.top };
  };

  const onDrawStart = (e) => {
    if (!drawMode) return;
    e.currentTarget.setPointerCapture(e.pointerId);
    pushHistory();
    currentStrokeRef.current = { color: drawColor, width: drawWidth, points: [stagePoint(e)] };
  };

  const onDrawMove = (e) => {
    if (!drawMode || !currentStrokeRef.current) return;
    currentStrokeRef.current.points.push(stagePoint(e));
    renderDrawCanvas([...strokes, currentStrokeRef.current]);
  };

  const onDrawEnd = () => {
    if (!drawMode || !currentStrokeRef.current) return;
    const s = currentStrokeRef.current;
    currentStrokeRef.current = null;
    if (s.points.length > 1) setStrokes((prev) => [...prev, s]);
  };

  const renderDrawCanvas = useCallback((allStrokes) => {
    const canvas = drawCanvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.lineCap = 'round'; ctx.lineJoin = 'round';
    for (const s of allStrokes) {
      if (s.points.length < 2) continue;
      ctx.strokeStyle = s.color; ctx.lineWidth = s.width;
      ctx.beginPath();
      ctx.moveTo(s.points[0].x, s.points[0].y);
      for (let i = 1; i < s.points.length; i++) ctx.lineTo(s.points[i].x, s.points[i].y);
      ctx.stroke();
    }
  }, []);

  useEffect(() => { renderDrawCanvas(strokes); }, [strokes, stage, renderDrawCanvas]);

  // === Text ===
  const saveText = () => {
    if (!textEdit) return;
    const content = (textEdit.content || '').trim();
    if (!content) { setTextEdit(null); return; }
    if (textEdit.id) {
      pushHistory();
      setElements((els) => els.map((el) => el.id === textEdit.id
        ? { ...el, content, color: textEdit.color, font: textEdit.font } : el));
    } else {
      addElement({ type: 'text', content, color: textEdit.color, font: textEdit.font, base: 30 });
    }
    setTextEdit(null);
  };

  // === Mentions ===
  useEffect(() => {
    if (panel === 'sticker' && stickerTab === 'mention' && connections === null) {
      api.get('/connections')
        .then((r) => setConnections(r.data.connections || []))
        .catch(() => setConnections([]));
    }
  }, [panel, stickerTab, connections]);

  // === GIF (GIPHY) ===
  const searchGifs = async (q) => {
    if (!GIPHY_KEY) return;
    try {
      const url = q
        ? `https://api.giphy.com/v1/gifs/search?api_key=${GIPHY_KEY}&q=${encodeURIComponent(q)}&limit=24&rating=pg-13`
        : `https://api.giphy.com/v1/gifs/trending?api_key=${GIPHY_KEY}&limit=24&rating=pg-13`;
      const res = await fetch(url);
      const data = await res.json();
      setGifResults(data.data || []);
    } catch (e) { setGifResults([]); }
  };

  useEffect(() => {
    if (panel === 'sticker' && stickerTab === 'gif' && GIPHY_KEY && gifResults.length === 0) searchGifs('');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [panel, stickerTab]);

  // === Export ===
  const handleDone = async () => {
    setExporting(true);
    try {
      const blob = await exportEditedImage({
        imageSrc,
        filter: buildFilter(preset, adjust),
        elements, strokes,
        stageW: stage.w, stageH: stage.h,
      });
      const meta = {
        mentions: elements.filter((e) => e.type === 'mention').map((e) => e.userId).filter(Boolean),
        hashtags: elements.filter((e) => e.type === 'hashtag').map((e) => e.content.replace('#', '')),
        location: elements.find((e) => e.type === 'location')?.content || null,
      };
      onDone(blob, meta);
    } catch (e) {
      console.error('Export error:', e);
    } finally {
      setExporting(false);
    }
  };

  if (!open) return null;

  const filterCss = buildFilter(preset, adjust);

  const renderElement = (el) => {
    const isSel = el.id === selectedId;
    const common = {
      onPointerDown: (e) => onElPointerDown(e, el.id),
      onPointerMove: (e) => onElPointerMove(e, el.id),
      onPointerUp: onElPointerUp,
      onPointerCancel: onElPointerUp,
      style: {
        position: 'absolute', left: el.x, top: el.y,
        transform: `translate(-50%, -50%) scale(${el.scale}) rotate(${(el.rotation || 0) * 57.2958}deg)`,
        touchAction: 'none', cursor: 'grab', userSelect: 'none', WebkitUserSelect: 'none',
        outline: isSel ? '1.5px dashed rgba(255,255,255,0.7)' : 'none',
        outlineOffset: 4, zIndex: 20,
      },
      'data-testid': `editor-element-${el.type}`,
    };
    if (el.type === 'sticker' || el.type === 'gif') {
      return <img key={el.id} src={el.src} alt="" draggable={false} {...common}
        style={{ ...common.style, width: el.base || 96 }} />;
    }
    if (el.type === 'emoji') {
      return <div key={el.id} {...common} style={{ ...common.style, fontSize: el.base || 48, lineHeight: 1 }}>{el.content}</div>;
    }
    if (el.type === 'text') {
      const font = FONTS.find((f) => f.id === el.font) || FONTS[0];
      return (
        <div key={el.id} {...common} style={{
          ...common.style, fontSize: el.base || 30, fontFamily: font.family, fontWeight: font.weight,
          color: el.color, textShadow: '0 1px 6px rgba(0,0,0,0.45)', whiteSpace: 'pre', textAlign: 'center',
        }}>{el.content}</div>
      );
    }
    // pills: mention / hashtag / location
    const label = el.type === 'location' ? `📍 ${el.content}` : el.content;
    return (
      <div key={el.id} {...common} style={{
        ...common.style, fontSize: el.base || 22, fontWeight: 700, color: '#fff',
        background: 'rgba(0,0,0,0.5)', padding: '8px 14px', borderRadius: 999, whiteSpace: 'nowrap',
        backdropFilter: 'blur(4px)',
      }}>{label}</div>
    );
  };

  const ToolBtn = ({ onClick, children, testid, active }) => (
    <button onClick={onClick} data-testid={testid}
      className={`p-2.5 rounded-full transition-colors ${active ? 'bg-white text-black' : 'bg-black/40 text-white'}`}>
      {children}
    </button>
  );

  return createPortal(
    <div className="fixed inset-x-0 top-0 z-[120] bg-black flex flex-col" style={{ height: '100dvh' }} data-testid="media-editor">
      {/* Top bar */}
      <div className="flex items-center justify-between px-3 shrink-0" style={{ paddingTop: 'max(env(safe-area-inset-top), 10px)', height: 'calc(max(env(safe-area-inset-top), 10px) + 52px)' }}>
        <div className="flex items-center gap-1.5">
          <ToolBtn onClick={onClose} testid="editor-close-btn"><X className="w-5 h-5" /></ToolBtn>
          <ToolBtn onClick={undo} testid="editor-undo-btn"><Undo2 className={`w-5 h-5 ${history.length ? '' : 'opacity-30'}`} /></ToolBtn>
          <ToolBtn onClick={redo} testid="editor-redo-btn"><Redo2 className={`w-5 h-5 ${future.length ? '' : 'opacity-30'}`} /></ToolBtn>
        </div>
        <div className="flex items-center gap-1.5">
          <ToolBtn onClick={() => setTextEdit({ content: '', color: '#FFFFFF', font: 'sans' })} testid="editor-text-btn"><Type className="w-5 h-5" /></ToolBtn>
          <ToolBtn onClick={() => { setPanel(panel === 'sticker' ? null : 'sticker'); setDrawMode(false); }} active={panel === 'sticker'} testid="editor-sticker-btn"><SmilePlus className="w-5 h-5" /></ToolBtn>
          <ToolBtn onClick={() => { setDrawMode(!drawMode); setPanel(null); setSelectedId(null); }} active={drawMode} testid="editor-draw-btn"><PenLine className="w-5 h-5" /></ToolBtn>
          <ToolBtn onClick={() => { setPanel(panel === 'filters' ? null : 'filters'); setDrawMode(false); }} active={panel === 'filters'} testid="editor-filters-btn"><Wand2 className="w-5 h-5" /></ToolBtn>
        </div>
      </div>

      {/* Stage */}
      <div className="flex-1 flex items-center justify-center overflow-hidden" onPointerDown={() => !drawMode && setSelectedId(null)}>
        {stage.w > 0 && (
          <div ref={stageRef} className="relative rounded-xl overflow-hidden" style={{ width: stage.w, height: stage.h, touchAction: 'none' }} data-testid="editor-stage">
            <img src={imageSrc} alt="" draggable={false} className="w-full h-full select-none" style={{ filter: filterCss }} />
            {/* Draw canvas */}
            <canvas
              ref={drawCanvasRef}
              width={stage.w} height={stage.h}
              className="absolute inset-0"
              style={{ touchAction: 'none', pointerEvents: drawMode ? 'auto' : 'none', zIndex: 15 }}
              onPointerDown={onDrawStart}
              onPointerMove={onDrawMove}
              onPointerUp={onDrawEnd}
              onPointerCancel={onDrawEnd}
              data-testid="editor-draw-canvas"
            />
            {elements.map(renderElement)}
          </div>
        )}
      </div>

      {/* Delete selected */}
      {selectedId && !drawMode && (
        <button onClick={deleteSelected} data-testid="editor-delete-element-btn"
          className="absolute left-1/2 -translate-x-1/2 bottom-24 z-30 p-3 rounded-full bg-red-500/90 text-white shadow-lg">
          <Trash2 className="w-5 h-5" />
        </button>
      )}

      {/* Draw toolbar */}
      {drawMode && (
        <div className="absolute left-0 right-0 bottom-20 z-30 flex items-center justify-center gap-2 px-4" data-testid="editor-draw-toolbar">
          <div className="flex items-center gap-1.5 bg-black/60 backdrop-blur rounded-full px-3 py-2">
            {DRAW_COLORS.map((c) => (
              <button key={c} onClick={() => setDrawColor(c)}
                className={`w-6 h-6 rounded-full border-2 ${drawColor === c ? 'border-white scale-110' : 'border-white/30'}`}
                style={{ backgroundColor: c }} data-testid={`draw-color-${c.replace('#', '')}`} />
            ))}
            <div className="w-px h-5 bg-white/20 mx-1" />
            {DRAW_WIDTHS.map((w) => (
              <button key={w} onClick={() => setDrawWidth(w)}
                className={`w-7 h-7 rounded-full flex items-center justify-center ${drawWidth === w ? 'bg-white/25' : ''}`}>
                <span className="rounded-full bg-white" style={{ width: w + 3, height: w + 3 }} />
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Bottom bar */}
      <div className="shrink-0 px-4 pt-2 flex items-center justify-end" style={{ paddingBottom: 'max(env(safe-area-inset-bottom), 14px)' }}>
        <button
          onClick={handleDone}
          disabled={exporting || stage.w === 0}
          className="px-8 h-11 rounded-full bg-gradient-to-r from-orange-500 via-pink-500 to-purple-500 text-white font-bold text-sm flex items-center gap-2 disabled:opacity-60"
          data-testid="editor-done-btn"
        >
          {exporting ? <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" /> : <Check className="w-4 h-4" />}
          Avanti
        </button>
      </div>

      {/* ===== Sticker / Emoji / GIF / @ / # / Location sheet ===== */}
      {panel === 'sticker' && (
        <div className="absolute left-0 right-0 bottom-0 z-40 bg-neutral-900/95 backdrop-blur-xl rounded-t-2xl max-h-[55dvh] flex flex-col" data-testid="editor-sticker-sheet" style={{ paddingBottom: 'max(env(safe-area-inset-bottom), 10px)' }}>
          <div className="flex gap-1 p-2 overflow-x-auto scrollbar-hide shrink-0">
            {[
              { id: 'emoji', label: 'Emoji' },
              { id: 'sticker', label: 'Sticker' },
              { id: 'gif', label: 'GIF' },
              { id: 'mention', label: '@' },
              { id: 'hashtag', label: '#' },
              { id: 'location', label: '📍' },
            ].map((tb) => (
              <button key={tb.id} onClick={() => setStickerTab(tb.id)}
                className={`px-4 py-1.5 rounded-full text-xs font-bold whitespace-nowrap ${stickerTab === tb.id ? 'bg-white text-black' : 'bg-white/10 text-white'}`}
                data-testid={`sticker-tab-${tb.id}`}>
                {tb.label}
              </button>
            ))}
            <div className="flex-1" />
            <button onClick={() => setPanel(null)} className="p-1.5 text-white/60"><X className="w-4 h-4" /></button>
          </div>
          <div className="overflow-y-auto px-3 pb-3">
            {stickerTab === 'emoji' && (
              <div className="grid grid-cols-6 gap-1">
                {EMOJIS.map((em) => (
                  <button key={em} onClick={() => addElement({ type: 'emoji', content: em, base: 48 })}
                    className="text-3xl p-2 hover:bg-white/10 rounded-xl" data-testid={`emoji-${em}`}>{em}</button>
                ))}
              </div>
            )}
            {stickerTab === 'sticker' && (
              <div className="grid grid-cols-4 gap-2">
                {ADE_STICKERS.map((st) => (
                  <button key={st.id} onClick={() => addElement({ type: 'sticker', src: st.src, base: 110 })}
                    className="p-2 bg-white/5 rounded-xl hover:bg-white/10" data-testid={`sticker-${st.id}`}>
                    <img src={st.src} alt={st.id} className="w-full h-16 object-contain" />
                  </button>
                ))}
              </div>
            )}
            {stickerTab === 'gif' && (
              GIPHY_KEY ? (
                <div>
                  <div className="flex items-center gap-2 bg-white/10 rounded-full px-3 py-2 mb-2">
                    <Search className="w-4 h-4 text-white/50" />
                    <input value={gifQuery} onChange={(e) => setGifQuery(e.target.value)}
                      onKeyDown={(e) => e.key === 'Enter' && searchGifs(gifQuery)}
                      placeholder="Cerca GIF..." className="bg-transparent text-white text-sm flex-1 outline-none placeholder:text-white/40"
                      data-testid="gif-search-input" />
                  </div>
                  <div className="grid grid-cols-3 gap-1.5">
                    {gifResults.map((g) => (
                      <button key={g.id} onClick={() => addElement({ type: 'gif', src: g.images?.fixed_height_small?.url || g.images?.original?.url, base: 140 })}
                        className="rounded-lg overflow-hidden bg-white/5">
                        <img src={g.images?.fixed_height_small?.url} alt="" className="w-full h-20 object-cover" />
                      </button>
                    ))}
                  </div>
                </div>
              ) : (
                <p className="text-white/50 text-sm text-center py-6" data-testid="gif-key-missing">Le GIF saranno disponibili a breve ✨</p>
              )
            )}
            {stickerTab === 'mention' && (
              <div>
                <div className="flex items-center gap-2 bg-white/10 rounded-full px-3 py-2 mb-2">
                  <AtSign className="w-4 h-4 text-white/50" />
                  <input value={mentionQuery} onChange={(e) => setMentionQuery(e.target.value)}
                    placeholder="Cerca tra i tuoi collegamenti..." className="bg-transparent text-white text-sm flex-1 outline-none placeholder:text-white/40"
                    data-testid="mention-search-input" />
                </div>
                <div className="space-y-1">
                  {(connections || [])
                    .map((conn) => conn.user || conn)
                    .filter((c) => !mentionQuery || (c.name || '').toLowerCase().includes(mentionQuery.toLowerCase()))
                    .slice(0, 12)
                    .map((c) => (
                      <button key={c.user_id} onClick={() => addElement({ type: 'mention', content: `@${(c.name || '').split(' ')[0]}`, userId: c.user_id, base: 22 })}
                        className="flex items-center gap-2 w-full p-2 rounded-xl hover:bg-white/10 text-left" data-testid={`mention-user-${c.user_id}`}>
                        <img src={c.picture || '/images/default-avatar.png'} alt="" className="w-8 h-8 rounded-full object-cover" />
                        <span className="text-white text-sm font-medium">{c.name}</span>
                      </button>
                    ))}
                  {connections !== null && connections.length === 0 && (
                    <p className="text-white/40 text-sm text-center py-4">Nessun collegamento</p>
                  )}
                </div>
              </div>
            )}
            {stickerTab === 'hashtag' && (
              <div className="flex items-center gap-2">
                <div className="flex items-center gap-2 bg-white/10 rounded-full px-3 py-2.5 flex-1">
                  <Hash className="w-4 h-4 text-white/50" />
                  <input value={hashtagInput} onChange={(e) => setHashtagInput(e.target.value.replace(/\s/g, ''))}
                    placeholder="hashtag" className="bg-transparent text-white text-sm flex-1 outline-none placeholder:text-white/40"
                    data-testid="hashtag-input" />
                </div>
                <button onClick={() => { if (hashtagInput) { addElement({ type: 'hashtag', content: `#${hashtagInput}`, base: 22 }); setHashtagInput(''); } }}
                  className="px-4 py-2.5 rounded-full bg-white text-black text-sm font-bold" data-testid="hashtag-add-btn">OK</button>
              </div>
            )}
            {stickerTab === 'location' && (
              <div className="flex items-center gap-2">
                <div className="flex items-center gap-2 bg-white/10 rounded-full px-3 py-2.5 flex-1">
                  <MapPin className="w-4 h-4 text-white/50" />
                  <input value={locationInput} onChange={(e) => setLocationInput(e.target.value)}
                    placeholder="Aggiungi luogo..." className="bg-transparent text-white text-sm flex-1 outline-none placeholder:text-white/40"
                    data-testid="location-input" />
                </div>
                <button onClick={() => { if (locationInput.trim()) { addElement({ type: 'location', content: locationInput.trim(), base: 22 }); setLocationInput(''); } }}
                  className="px-4 py-2.5 rounded-full bg-white text-black text-sm font-bold" data-testid="location-add-btn">OK</button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ===== Filters / Adjust sheet ===== */}
      {panel === 'filters' && (
        <div className="absolute left-0 right-0 bottom-0 z-40 bg-neutral-900/95 backdrop-blur-xl rounded-t-2xl" data-testid="editor-filters-sheet" style={{ paddingBottom: 'max(env(safe-area-inset-bottom), 10px)' }}>
          <div className="flex gap-1 p-2 items-center">
            <button onClick={() => setFilterTab('filters')} className={`px-4 py-1.5 rounded-full text-xs font-bold ${filterTab === 'filters' ? 'bg-white text-black' : 'bg-white/10 text-white'}`} data-testid="filter-tab-filters">Filtri</button>
            <button onClick={() => setFilterTab('adjust')} className={`px-4 py-1.5 rounded-full text-xs font-bold ${filterTab === 'adjust' ? 'bg-white text-black' : 'bg-white/10 text-white'}`} data-testid="filter-tab-adjust">Regola</button>
            <div className="flex-1" />
            <button onClick={() => setPanel(null)} className="p-1.5 text-white/60"><X className="w-4 h-4" /></button>
          </div>
          {filterTab === 'filters' ? (
            <div className="flex gap-2 overflow-x-auto scrollbar-hide px-3 pb-3">
              {FILTER_PRESETS.map((p) => (
                <button key={p.id} onClick={() => { pushHistory(); setPreset(p.id); }}
                  className="flex flex-col items-center gap-1 shrink-0" data-testid={`filter-${p.id}`}>
                  <div className={`w-16 h-16 rounded-xl overflow-hidden border-2 ${preset === p.id ? 'border-pink-500' : 'border-transparent'}`}>
                    <img src={imageSrc} alt="" className="w-full h-full object-cover" style={{ filter: p.css }} />
                  </div>
                  <span className={`text-[10px] font-semibold ${preset === p.id ? 'text-pink-400' : 'text-white/60'}`}>{p.name}</span>
                </button>
              ))}
            </div>
          ) : (
            <div className="px-4 pb-4 space-y-3">
              {[
                { key: 'brightness', label: 'Luminosità', min: 0.5, max: 1.5 },
                { key: 'contrast', label: 'Contrasto', min: 0.5, max: 1.5 },
                { key: 'saturate', label: 'Saturazione', min: 0, max: 2 },
                { key: 'warmth', label: 'Calore', min: 0, max: 0.6 },
              ].map((s) => (
                <div key={s.key} className="flex items-center gap-3">
                  <span className="text-white/70 text-xs w-20 shrink-0">{s.label}</span>
                  <Slider
                    value={[adjust[s.key]]}
                    min={s.min} max={s.max} step={0.01}
                    onValueChange={([v]) => setAdjust((a) => ({ ...a, [s.key]: v }))}
                    className="flex-1"
                    data-testid={`adjust-${s.key}`}
                  />
                </div>
              ))}
              <button onClick={() => { pushHistory(); setAdjust(DEFAULT_ADJUST); }} className="text-white/50 text-xs underline" data-testid="adjust-reset-btn">Reimposta</button>
            </div>
          )}
        </div>
      )}

      {/* ===== Text editor overlay ===== */}
      {textEdit && (
        <div className="absolute inset-0 z-50 bg-black/80 flex flex-col" data-testid="editor-text-overlay">
          <div className="flex justify-end px-4" style={{ paddingTop: 'max(env(safe-area-inset-top), 12px)' }}>
            <button onClick={saveText} className="px-5 py-2 rounded-full bg-white text-black text-sm font-bold" data-testid="text-done-btn">Fatto</button>
          </div>
          <div className="flex-1 flex items-center justify-center px-6">
            <textarea
              autoFocus
              value={textEdit.content}
              onChange={(e) => setTextEdit((t) => ({ ...t, content: e.target.value }))}
              rows={3}
              className="w-full bg-transparent text-center outline-none resize-none placeholder:text-white/30"
              style={{
                fontSize: 30, color: textEdit.color,
                fontFamily: (FONTS.find((f) => f.id === textEdit.font) || FONTS[0]).family,
                fontWeight: (FONTS.find((f) => f.id === textEdit.font) || FONTS[0]).weight,
                textShadow: '0 1px 6px rgba(0,0,0,0.45)',
              }}
              placeholder="Scrivi..."
              data-testid="text-input"
            />
          </div>
          <div className="space-y-3 px-4" style={{ paddingBottom: 'max(env(safe-area-inset-bottom), 16px)' }}>
            <div className="flex gap-2 overflow-x-auto scrollbar-hide">
              {FONTS.map((f) => (
                <button key={f.id} onClick={() => setTextEdit((t) => ({ ...t, font: f.id }))}
                  className={`px-4 py-1.5 rounded-full text-xs whitespace-nowrap ${textEdit.font === f.id ? 'bg-white text-black' : 'bg-white/15 text-white'}`}
                  style={{ fontFamily: f.family, fontWeight: f.weight }}
                  data-testid={`font-${f.id}`}>
                  {f.name}
                </button>
              ))}
            </div>
            <div className="flex gap-2.5 justify-center">
              {TEXT_COLORS.map((c) => (
                <button key={c} onClick={() => setTextEdit((t) => ({ ...t, color: c }))}
                  className={`w-7 h-7 rounded-full border-2 ${textEdit.color === c ? 'border-pink-500 scale-110' : 'border-white/40'}`}
                  style={{ backgroundColor: c }} data-testid={`text-color-${c.replace('#', '')}`} />
              ))}
            </div>
          </div>
        </div>
      )}
    </div>,
    document.body
  );
}
